export interface ScheduleFormData {
  frequency: 'daily' | 'weekly' | 'monthly';
  weeklyDays: number[];
  monthlyDays: number[];
  timeEST: string;
  enabled: boolean;
  /** Also show the report table in the email body. Offered for event reports only. */
  includeTableInBody?: boolean;
}

export interface EligibleRecipient {
  _id: string;
  privateName: string;
  email: string;
  roleNumber: number;
  role: string;
}

/** GET report-notifications/reports/:reportId/recipients. Every caller gets the
 *  same full row: the server's ReportNotificationController.getRecipients only
 *  drops internal users (filterInternalUsers) and projects nothing, so there is
 *  no ids-only reply for a caller who may not schedule reports. Only userId is
 *  required because it is the only field anything here reads - the recipient
 *  lists map straight to a Set of ids; the rest is informational. */
export interface ReportRecipient {
  userId: string;
  subscriptionId?: string;
  privateName?: string;
  email?: string;
  role?: string;
  roleNumber?: number;
  facilityIds?: number[];
}

// --- Region-wide overview (GET report-notifications/schedules) ---

export interface ScheduleOverviewRecipient {
  userId: string;
  privateName: string;
  email: string;
}

export interface ScheduleOverviewLastRun {
  startedAt: string | null;
  executedAt: string | null;
  phase: 'compiling' | 'compiled' | 'delivering' | 'done' | 'failure' | 'skipped';
  status: 'success' | 'partial_failure' | 'failure' | null;
  /** Why the run delivered nothing. Shown verbatim to the admin. */
  outcomeReason?: string | null;
  sentCount: number;
  errorCount: number;
  skippedCount: number;
}

export interface ScheduleOverviewRow {
  reportType: string;
  reportId: string;
  reportName: string | null;
  /** false = the server could not resolve this report any more (deleted, or unknown type). */
  exists: boolean;
  frequency: 'daily' | 'weekly' | 'monthly';
  weeklyDays?: number[];
  monthlyDays?: number[];
  timeEST: string;
  enabled: boolean;
  nextFireAt: string | null;
  lastExecutedAt: string | null;
  lastRun: ScheduleOverviewLastRun | null;
  recipients: ScheduleOverviewRecipient[];
  updatedAt: string | null;
  updatedBy: string | null;
}

// Wire reportType -> the board's pastel band (1..6). Keyed by TYPE, not by row
// position, so a report keeps its colour as the list grows, is filtered or is
// re-sorted - the same contract the High Risk index gives a facility.
export const REPORT_TYPE_BANDS: Record<string, number> = {
  high_risk_patients: 6,   // the logo's teal - this is the High Risk family
  manager_dashboard: 1,
  advanced_directives: 3,
  custom_assessment: 2,
  adt: 4,
  event_report: 5,
  discharge_plan: 1,       // the bed board's blue - the Discharge Plan is its child
};

// Wire reportType -> label shown in the Scheduled Emails list. An unmapped type
// falls back to a de-underscored version of the wire value, so a newly plugged-in
// report shows up readably before this map is updated.
export const REPORT_TYPE_LABELS: Record<string, string> = {
  high_risk_patients: 'High Risk Patients',
  manager_dashboard: 'Referrals Summary',
  advanced_directives: 'Advanced Directives',
  custom_assessment: 'Assessment Report',
  adt: 'ADT Report',
  event_report: 'Event Report',
  discharge_plan: 'Discharge Plan',
};

// AD report-type identifiers. Wire form is the snake-case server contract;
// deep-link form is the dashed value used in /manageb/patient-assessments?reportType=…
export const AD_REPORT_TYPE_WIRE = 'advanced_directives';
export const AD_REPORT_TYPE_DEEP_LINK = 'advanced-directives';
export const AD_REPORT_ID = 'default';

export const CUSTOM_ASSESSMENT_REPORT_TYPE_WIRE = 'custom_assessment';

// High Risk Patients scheduled email - single-instance report, like AD.
export const HIGH_RISK_REPORT_TYPE_WIRE = 'high_risk_patients';
export const HIGH_RISK_REPORT_ID = 'default';

// Discharge Plan scheduled email - single-instance, like High Risk: the list of
// expected discharges across the recipient's facilities, as a file.
export const DISCHARGE_PLAN_REPORT_TYPE_WIRE = 'discharge_plan';
export const DISCHARGE_PLAN_REPORT_ID = 'default';

// The daily manager email. Its schedule is re-seeded by the server on startup,
// which the Scheduled Emails screen warns about before a delete.
export const MANAGER_DASHBOARD_REPORT_TYPE_WIRE = 'manager_dashboard';
